// Base-page interactions: theme + CRT toggles, nav highlight, auto-submitting
// filter forms, watchlist + alerts nav modals. Vanilla, no framework.
import { initWatchlist } from "./watchlist.js";
import { initAlerts } from "./alerts.js";
import { t } from "../client/i18n.js";

initWatchlist();
initAlerts();

// Warm the HTML cache for the other pages on EVERY page load. The HTTP cache
// makes this self-throttling under the server's max-age=600 +
// stale-while-revalidate=3600 policy: a fresh copy costs zero network, a
// stale one returns instantly from cache while the browser refreshes it in
// the background (browsers honor SWR for fetch() but NOT for navigations —
// so this warm-up is what keeps every nav click landing on a fresh-enough
// copy). Sequential, low-priority, and idle-deferred so it never competes
// with this page's own assets.
const PREFETCH = [
  "/", "/forsale", "/bids", "/sales", "/attributes",
  "/leaderboard", "/rarity", "/recents", "/topsales",
];
const saveData =
  (navigator as { connection?: { saveData?: boolean } }).connection?.saveData === true;
if (!saveData) {
  const quiet = (url: string) =>
    fetch(url, { priority: "low" } as RequestInit).catch(() => {});
  const warm = async () => {
    // The neighbouring pages of a paginated list first (the arrows are the
    // likeliest next click), then the nav pages…
    for (const a of document.querySelectorAll<HTMLAnchorElement>('.pagination a[rel="prev"], .pagination a[rel="next"]')) {
      await quiet(a.getAttribute("href")!);
    }
    for (const href of PREFETCH) {
      if (href !== location.pathname) await quiet(href);
    }
    // …then every hashed module (entries + shared chunks) from the inline
    // data block (see base.html — no manifest.json fetch), so navigating to
    // any page needs zero script fetches. Files already loaded by this page
    // resolve from the immutable cache for free.
    try {
      const files = JSON.parse(
        document.getElementById("asset-files")?.textContent ?? "[]",
      ) as string[];
      for (const f of files) await quiet(f);
    } catch {
      /* best-effort */
    }
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => void warm(), { timeout: 5000 });
  } else {
    setTimeout(() => void warm(), 2000);
  }
}

// Filter panels apply instantly: any change inside a form[data-autofilter]
// submits it (plain GET — server-rendered as always). APPLY buttons stay in
// the HTML as a no-JS fallback; CSS hides them via html[data-js] (set by the
// inline head script) so they never paint and then vanish.
document.addEventListener("change", (e) => {
  const form = (e.target as Element).closest<HTMLFormElement>("form[data-autofilter]");
  if (form) form.submit();
});

// Header wallet button: label + visibility are set inline in base.html at
// parse time (no post-paint pop). Connected → toggles a dropdown with
// MY PROFILE (the wallet's own account page), SEND (transfer monkes to another
// address) and WALLET (connect/disconnect menu); disconnected → lazily import
// connect.js and open the picker.
const walletBtn = document.getElementById("wallet-toggle");
if (walletBtn) {
  walletBtn.onclick = async () => {
    const { currentAccount, walletMenu } = await import("./connect.js");
    const account = currentAccount();
    if (!account) {
      walletMenu();
      return;
    }
    const open = document.getElementById("wallet-dropdown");
    if (open) {
      open.remove();
      return;
    }
    const menu = document.createElement("div");
    menu.id = "wallet-dropdown";
    menu.className = "wallet-dropdown";
    const profile = document.createElement("a");
    profile.href = `/account/${encodeURIComponent(account.ordinalsAddress)}`;
    profile.textContent = t("client.site.profile");
    const wal = document.createElement("button");
    wal.type = "button";
    wal.textContent = t("client.site.wallet");
    wal.onclick = () => {
      menu.remove();
      walletMenu();
    };
    const send = document.createElement("button");
    send.type = "button";
    send.textContent = t("client.site.send");
    send.onclick = async () => {
      menu.remove();
      const { sendModal } = await import("./send.js");
      sendModal(account);
    };
    menu.append(profile, send, wal);
    walletBtn.parentElement!.appendChild(menu);
    // Dismiss on any click outside the menu (the button's own toggle handles
    // clicks on it) or on Escape. The opening click is still bubbling, so
    // ignore it via the target check.
    const dismiss = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menu.contains(t) || walletBtn.contains(t)) return;
      menu.remove();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") menu.remove();
    };
    document.addEventListener("click", dismiss);
    document.addEventListener("keydown", onKey);
    // Clean the listeners up whenever the menu leaves the DOM, however that
    // happened (outside click, Escape, re-toggle, WALLET).
    new MutationObserver((_, obs) => {
      if (!menu.isConnected) {
        document.removeEventListener("click", dismiss);
        document.removeEventListener("keydown", onKey);
        obs.disconnect();
      }
    }).observe(walletBtn.parentElement!, { childList: true });
  };
}

// Theme toggle cycles dark → sepia → light → dark; sepia is the default
// (stamped by the head bootstrap when no preference is stored — "light" is
// the attribute-less palette); persisted per browser.
const themeBtn = document.getElementById("theme-toggle");
if (themeBtn) {
  const THEMES = ["light", "dark", "sepia"] as const;
  type Theme = (typeof THEMES)[number];
  const GLYPHS: Record<Theme, string> = { light: "☀", dark: "☾", sepia: "▩" };
  const current = (): Theme => {
    const t = document.documentElement.dataset.theme;
    return THEMES.includes(t as Theme) ? (t as Theme) : "light";
  };
  const next = () => THEMES[(THEMES.indexOf(current()) + 1) % THEMES.length]!;
  const label = () => {
    themeBtn.textContent = GLYPHS[current()];
    themeBtn.title = t("client.site.theme_title", { current: current(), next: next() });
  };
  label();
  themeBtn.onclick = () => {
    const t = next();
    if (t === "light") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
    localStorage.setItem("mdx-theme", t);
    label();
  };
}

// Language toggle: remember the choice in the cookie (a year) and reload;
// on a /zh or /en page swap to the other language's URL instead.
const langBtn = document.getElementById("lang-toggle");
if (langBtn) {
  langBtn.onclick = () => {
    document.cookie = `mdx-lang=${langBtn.dataset.lang}; path=/; max-age=31536000; samesite=lax`;
    if (langBtn.dataset.href) location.href = langBtn.dataset.href;
    else location.reload();
  };
}

// CRT effect toggle — off by default; the CSS keys off html[data-crt="1"].
const crt = document.getElementById("crt-toggle");
if (crt) {
  const setState = (on: boolean) => {
    document.documentElement.dataset.crt = on ? "1" : "0";
    document.body.classList.toggle("crt", on);
    crt.setAttribute("aria-pressed", on ? "true" : "false");
  };
  setState(localStorage.getItem("mdx-crt") === "1");
  crt.onclick = () => {
    const on = document.documentElement.dataset.crt !== "1";
    setState(on);
    localStorage.setItem("mdx-crt", on ? "1" : "0");
  };
}
